import { MAX_CHAT_NOTE_LENGTH } from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { useId, useState } from "react";

import {
  ChatDeleteMessageButton,
  ChatMuteButton,
  ChatUnmuteButton,
} from "../components/ChatMuteControl";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { TableMessageRow } from "../components/TableMessageRow";
import {
  CHAT_REPORT_CATEGORY_LABELS,
  CHAT_REPORT_STATUS_LABELS,
  CHAT_TAB_LABELS,
  CHAT_TABS,
  type ChatReportRow,
  type ChatReportStatus,
  type ChatSearch,
  type ChatTab,
  deletedByLabel,
  isChatReportStatus,
  isChatTab,
  resolveChatTab,
  useInvalidateChat,
} from "../lib/chat";
import { formatDate } from "../lib/format";
import { trpcClient, useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/** The one room today; the tuple in `@loot-os/api-contract` is where a second would come from. */
const ROOM = "general" as const;
/** How often the room tab re-reads the room: staff watch it live without a realtime token. */
const ROOM_POLL_MS = 5_000;

/**
 * Storefront chat moderation for the active environment (docs/chat.md), rendered on Storefront →
 * Chat under the switch: the room as staff see it, deleted messages included; the report queue
 * shoppers fill; and the mutes in force. Every member reads; editors delete, mute, unmute and
 * close reports. The tab rides `?tab=`. Nothing here depends on the chat switch, so reports stay
 * workable with chat off.
 */
export function ChatModeration() {
  const router = useRouter();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const search = useSearch({ strict: false }) as ChatSearch;
  const activeTab = resolveChatTab(search.tab);

  const setTab = (tab: ChatTab) => {
    void router.navigate({
      to: "/tenants/$tenantId/storefront/chat",
      params: { tenantId },
      search: { tab },
      replace: true,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isChatTab(value)) setTab(value);
        }}
        className="gap-4"
      >
        <TabsList>
          {CHAT_TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {CHAT_TAB_LABELS[tab]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="room">
          <RoomTab tenantId={tenantId} />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsTab tenantId={tenantId} />
        </TabsContent>
        <TabsContent value="mutes">
          <MutesTab tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UserLink({
  tenantId,
  endUserId,
  children,
}: {
  tenantId: string;
  endUserId: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to="/tenants/$tenantId/users/$userId"
      params={{ tenantId, userId: endUserId }}
      className="font-medium hover:underline"
    >
      {children}
    </Link>
  );
}

/** The room, newest first, re-read every five seconds; older pages load on demand. */
function RoomTab({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  // `chat.messages` pages on `before`, not tRPC's `cursor`, so the infinite query is built here.
  // Its key extends the procedure's, so `useInvalidateChat` (the `chat` path key) reaches it.
  const query = useInfiniteQuery({
    queryKey: [...trpc.chat.messages.queryKey({ roomId: ROOM }), "pages"],
    queryFn: ({ pageParam }) =>
      trpcClient.chat.messages.query({ roomId: ROOM, limit: 50, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: ROOM_POLL_MS,
  });
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const columns = 4;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Sent</TableHead>
              <TableHead className="w-40">Shopper</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-40 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && <TableMessageRow colSpan={columns}>Loading…</TableMessageRow>}
            {query.error && (
              <TableMessageRow colSpan={columns} tone="error">
                {query.error.message}
              </TableMessageRow>
            )}
            {query.isSuccess && rows.length === 0 && (
              <TableMessageRow colSpan={columns}>Nobody has said anything yet.</TableMessageRow>
            )}
            {rows.map((message) => (
              <TableRow key={message.messageId}>
                <TableCell className="align-top text-muted-foreground">
                  {formatDate(message.createdAt)}
                </TableCell>
                <TableCell className="align-top">
                  <UserLink tenantId={tenantId} endUserId={message.endUserId}>
                    @{message.displayName}
                  </UserLink>
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  {message.replyTo && (
                    <p className="mb-1 border-l-2 pl-2 text-xs text-muted-foreground">
                      Replying to @{message.replyTo.displayName}
                      {message.replyTo.text ? `: ${message.replyTo.text}` : " (deleted)"}
                    </p>
                  )}
                  {message.deletedAt ? (
                    <span className="text-muted-foreground italic">
                      {deletedByLabel(message.deletedBy)}
                    </span>
                  ) : (
                    <span className="break-words">{message.text}</span>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex justify-end gap-1">
                    {!message.deletedAt && (
                      <ChatDeleteMessageButton roomId={ROOM} messageId={message.messageId} />
                    )}
                    <ChatMuteButton
                      endUserId={message.endUserId}
                      name={message.displayName}
                      variant="ghost"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <LoadMoreButton
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

/** The report queue, one status at a time, newest first. */
function ReportsTab({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  const [status, setStatus] = useState<ChatReportStatus>("OPEN");
  const query = useInfiniteQuery({
    queryKey: [...trpc.chat.reports.queryKey({ status }), "pages"],
    queryFn: ({ pageParam }) =>
      trpcClient.chat.reports.query({ status, limit: 50, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const columns = 5;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Label htmlFor="chat-report-status" className="text-sm text-muted-foreground">
            Status
          </Label>
          <Select
            value={status}
            items={CHAT_REPORT_STATUS_LABELS}
            onValueChange={(value) => {
              if (isChatReportStatus(value)) setStatus(value);
            }}
          >
            <SelectTrigger id="chat-report-status" className="w-40" aria-label="Report status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CHAT_REPORT_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Reported</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="w-40">Shopper</TableHead>
              <TableHead>Message and note</TableHead>
              <TableHead className="w-56 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && <TableMessageRow colSpan={columns}>Loading…</TableMessageRow>}
            {query.error && (
              <TableMessageRow colSpan={columns} tone="error">
                {query.error.message}
              </TableMessageRow>
            )}
            {query.isSuccess && rows.length === 0 && (
              <TableMessageRow colSpan={columns}>
                {status === "OPEN"
                  ? "No open reports."
                  : `No ${CHAT_REPORT_STATUS_LABELS[status].toLowerCase()} reports.`}
              </TableMessageRow>
            )}
            {rows.map((report) => (
              <ReportRow key={report.reportId} report={report} tenantId={tenantId} />
            ))}
          </TableBody>
        </Table>
        <LoadMoreButton
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

function ReportRow({ report, tenantId }: { report: ChatReportRow; tenantId: string }) {
  const open = report.status === "OPEN";
  return (
    <TableRow>
      <TableCell className="align-top text-muted-foreground">
        {formatDate(report.createdAt)}
      </TableCell>
      <TableCell className="align-top">
        <Badge variant="outline">
          {CHAT_REPORT_CATEGORY_LABELS[report.category] ?? report.category}
        </Badge>
      </TableCell>
      <TableCell className="align-top">
        <UserLink tenantId={tenantId} endUserId={report.reportedEndUserId}>
          View shopper
        </UserLink>
      </TableCell>
      <TableCell className="align-top whitespace-normal">
        <p className="break-words">
          {report.messageText || <span className="text-muted-foreground italic">No text</span>}
        </p>
        {report.description && (
          <p className="mt-1 text-xs text-muted-foreground">Reporter: {report.description}</p>
        )}
        {!open && (
          <p className="mt-1 text-xs text-muted-foreground">
            {CHAT_REPORT_STATUS_LABELS[report.status]}
            {report.resolvedAt ? ` ${formatDate(report.resolvedAt)}` : ""}
            {report.resolutionNote ? ` · ${report.resolutionNote}` : ""}
          </p>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-wrap justify-end gap-1">
          {open && (
            <>
              <CloseReportButton reportId={report.reportId} status="RESOLVED" />
              <CloseReportButton reportId={report.reportId} status="DISMISSED" />
            </>
          )}
          <ChatDeleteMessageButton roomId={ROOM} messageId={report.messageId} />
          <ChatMuteButton endUserId={report.reportedEndUserId} variant="ghost" />
        </div>
      </TableCell>
    </TableRow>
  );
}

/** Resolves or dismisses an open report, with an optional note. Editors and above. */
function CloseReportButton({
  reportId,
  status,
}: {
  reportId: string;
  status: "RESOLVED" | "DISMISSED";
}) {
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const invalidate = useInvalidateChat();
  const id = useId();
  const [note, setNote] = useState("");
  const close = useMutation(
    trpc.chat.closeReport.mutationOptions({
      onSuccess: () => {
        toast.success(status === "RESOLVED" ? "Report resolved." : "Report dismissed.");
        setNote("");
        void invalidate();
      },
    }),
  );
  if (!can("editor")) return null;
  const verb = status === "RESOLVED" ? "Resolve" : "Dismiss";
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant={status === "RESOLVED" ? "outline" : "ghost"}
            size="sm"
            disabled={close.isPending}
          />
        }
      >
        {verb}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{verb} this report?</AlertDialogTitle>
          <AlertDialogDescription>
            {status === "RESOLVED"
              ? "You acted on it: the message was deleted, the shopper muted, or both."
              : "Nothing needed doing. The report leaves the open queue."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-note`}>Note (optional)</Label>
          <Input
            id={`${id}-note`}
            maxLength={MAX_CHAT_NOTE_LENGTH}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close.mutate({ reportId, status, note: note.trim() || undefined })}
          >
            {verb}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The mutes in force, newest first. */
function MutesTab({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  const query = useQuery(trpc.chat.mutes.queryOptions());
  const rows = query.data ?? [];
  const columns = 5;

  return (
    <Card>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Shopper</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="w-44">Muted</TableHead>
              <TableHead className="w-44">Ends</TableHead>
              <TableHead className="w-28 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && <TableMessageRow colSpan={columns}>Loading…</TableMessageRow>}
            {query.error && (
              <TableMessageRow colSpan={columns} tone="error">
                {query.error.message}
              </TableMessageRow>
            )}
            {query.isSuccess && rows.length === 0 && (
              <TableMessageRow colSpan={columns}>Nobody is muted.</TableMessageRow>
            )}
            {rows.map((mute) => (
              <TableRow key={mute.endUserId}>
                <TableCell>
                  <UserLink tenantId={tenantId} endUserId={mute.endUserId}>
                    {mute.username ? `@${mute.username}` : (mute.externalId ?? mute.endUserId)}
                  </UserLink>
                  {mute.username && mute.externalId && (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {mute.externalId}
                    </span>
                  )}
                </TableCell>
                <TableCell className={mute.reason ? "whitespace-normal" : "text-muted-foreground"}>
                  {mute.reason ?? "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(mute.createdAt)}
                </TableCell>
                <TableCell className={mute.expiresAt ? undefined : "text-muted-foreground"}>
                  {mute.expiresAt ? formatDate(mute.expiresAt) : "Until unmuted"}
                </TableCell>
                <TableCell className="text-right">
                  <ChatUnmuteButton endUserId={mute.endUserId} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
